import { useCallback, useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { Locate, ZoomOut, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { MAPBOX_TOKEN, MAPBOX_STYLES } from '@/config/mapbox'
import { useTheme } from '@/context/ThemeContext'

export interface LocationMarker {
  id: string
  lat: number
  lng: number
  label: string
}

interface MapViewProps {
  restaurantCoords: { lat: number; lng: number }
  userCoords?: { lat: number; lng: number } | null
  locations?: LocationMarker[]
  selectedLocationId?: string | null
  className?: string
}

const PIN_W = 28
const PIN_H = 36

function createPinElement(active: boolean): HTMLDivElement {
  const el = document.createElement('div')
  el.style.width = `${PIN_W}px`
  el.style.height = `${PIN_H}px`
  el.style.cursor = 'pointer'
  const primary = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
  const fillColor = active ? `hsl(${primary})` : '#999'
  el.innerHTML = `<svg width="${PIN_W}" height="${PIN_H}" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.268 21.732 0 14 0z" fill="${fillColor}"/>
    <circle cx="14" cy="13" r="5" fill="white"/>
  </svg>`
  if (!active) {
    el.style.opacity = '0.7'
  }
  return el
}

export function MapView({ restaurantCoords, userCoords, locations, selectedLocationId, className = '' }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null)
  const pinMarkersRef = useRef<mapboxgl.Marker[]>([])
  const { theme } = useTheme()
  const [isLocating, setIsLocating] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)

  const isDark = theme === 'dark'

  // Init map (runs once)
  useEffect(() => {
    if (!containerRef.current) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: isDark ? MAPBOX_STYLES.dark : MAPBOX_STYLES.light,
      center: [restaurantCoords.lng, restaurantCoords.lat],
      zoom: 12,
      attributionControl: false,
      cooperativeGestures: true,
    })

    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right')
    map.on('load', () => setMapLoaded(true))

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
      pinMarkersRef.current = []
      userMarkerRef.current = null
      setMapLoaded(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync pin markers whenever locations, selection, or load state changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    // Remove existing pin markers
    pinMarkersRef.current.forEach((m) => m.remove())
    pinMarkersRef.current = []

    if (locations && locations.length > 0) {
      locations.forEach((loc) => {
        const isActive = !selectedLocationId || selectedLocationId === loc.id
        const el = createPinElement(isActive)
        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([loc.lng, loc.lat])
          .addTo(map)
        pinMarkersRef.current.push(marker)
      })
    } else {
      const el = createPinElement(true)
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([restaurantCoords.lng, restaurantCoords.lat])
        .addTo(map)
      pinMarkersRef.current.push(marker)
    }
  }, [mapLoaded, locations, selectedLocationId, restaurantCoords])

  // Sync user marker whenever userCoords or load state changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    if (userMarkerRef.current) {
      userMarkerRef.current.remove()
      userMarkerRef.current = null
    }

    if (userCoords) {
      const dotEl = document.createElement('div')
      dotEl.className = 'user-location-dot'
      userMarkerRef.current = new mapboxgl.Marker({ element: dotEl })
        .setLngLat([userCoords.lng, userCoords.lat])
        .addTo(map)
    }
  }, [mapLoaded, userCoords])

  // Fit bounds to show all points — runs when any relevant data changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    const bounds = new mapboxgl.LngLatBounds()

    if (locations && locations.length > 0) {
      locations.forEach((l) => bounds.extend([l.lng, l.lat]))
    } else {
      bounds.extend([restaurantCoords.lng, restaurantCoords.lat])
    }

    if (userCoords) {
      bounds.extend([userCoords.lng, userCoords.lat])
    }

    map.fitBounds(bounds, { padding: 60, maxZoom: 14 })
  }, [mapLoaded, locations, userCoords, restaurantCoords])

  // Theme switch
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    map.setStyle(isDark ? MAPBOX_STYLES.dark : MAPBOX_STYLES.light)
  }, [isDark, mapLoaded])

  const handleLocateMe = useCallback(() => {
    const map = mapRef.current
    if (!map) return

    if (userCoords) {
      map.flyTo({ center: [userCoords.lng, userCoords.lat], zoom: 15 })
      return
    }

    if (!navigator.geolocation) {
      toast.error('Location is not supported by your browser')
      return
    }

    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setIsLocating(false)
        const coords = { lng: pos.coords.longitude, lat: pos.coords.latitude }
        const dotEl = document.createElement('div')
        dotEl.className = 'user-location-dot'
        if (userMarkerRef.current) userMarkerRef.current.remove()
        userMarkerRef.current = new mapboxgl.Marker({ element: dotEl })
          .setLngLat([coords.lng, coords.lat])
          .addTo(map)
        map.flyTo({ center: [coords.lng, coords.lat], zoom: 15 })
      },
      (err) => {
        setIsLocating(false)
        if (err.code === err.PERMISSION_DENIED) {
          toast.error('Location access denied. Enable it in your browser settings.')
        } else {
          toast.error('Could not get your location. Try again.')
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }, [userCoords])

  const handleFitAll = useCallback(() => {
    const map = mapRef.current
    if (!map) return

    const bounds = new mapboxgl.LngLatBounds()
    if (locations && locations.length > 0) {
      locations.forEach((l) => bounds.extend([l.lng, l.lat]))
    } else {
      bounds.extend([restaurantCoords.lng, restaurantCoords.lat])
    }
    if (userCoords) {
      bounds.extend([userCoords.lng, userCoords.lat])
    }
    map.fitBounds(bounds, { padding: 60, maxZoom: 15 })
  }, [userCoords, restaurantCoords, locations])

  const controlBtn =
    'w-9 h-9 rounded-lg bg-background/95 backdrop-blur-md border border-border/50 shadow-[0_2px_8px_rgba(0,0,0,0.08)] flex items-center justify-center transition-all duration-200 cursor-pointer hover:bg-background hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:scale-105 active:scale-95'

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5 p-1 rounded-xl bg-background/60 backdrop-blur-xl border border-border/30 shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
        <button
          onClick={handleLocateMe}
          className={controlBtn}
          aria-label="Find my location"
          disabled={isLocating}
        >
          {isLocating
            ? <Loader2 className="h-4 w-4 text-foreground/80 animate-spin" />
            : <Locate className="h-4 w-4 text-foreground/80" />
          }
        </button>
        <div className="w-5 mx-auto border-t border-border/40" />
        <button
          onClick={handleFitAll}
          className={controlBtn}
          aria-label="Zoom out to see full area"
        >
          <ZoomOut className="h-4 w-4 text-foreground/80" />
        </button>
      </div>
    </div>
  )
}
